import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../App";
import { usePmTestMode } from "../hooks/usePmTestMode";
import { toast } from "sonner";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Plus,
  Calendar,
  List,
  Clock,
  Check,
  X,
  ChevronDown,
  Filter,
  RefreshCw,
  Trash2,
  User,
  CalendarDays,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Building2,
  Wallet
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Checkbox } from "../components/ui/checkbox";
import ThreeMonthOutlook from "../components/leaves/ThreeMonthOutlook";

const VIEW_MODES = [
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "outlook", label: "3-Month Outlook", icon: CalendarDays },
  { id: "list", label: "List View", icon: List },
  { id: "pending", label: "Pending Requests", icon: Clock },
  { id: "balances", label: "PTO Balances", icon: Wallet },
];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateRange(start, end) {
  if (!start) return "";
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : startDate;
  
  if (startDate.toDateString() === endDate.toDateString()) {
    return formatDate(start);
  }
  
  return `${formatDate(start)} - ${formatDate(end)}`;
}

function getStatusBadge(status) {
  switch (status) {
    case "approved":
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>;
    case "denied":
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Denied</Badge>;
    case "pending":
      return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    case "cancelled":
      return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30"><X className="w-3 h-3 mr-1" />Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

// Calendar Component
function LeaveCalendar({ events, onEventClick, onDateClick, loading }) {
  const handleEventClick = (info) => {
    onEventClick(info.event.extendedProps);
  };

  const handleDateClick = (info) => {
    onDateClick(info.dateStr);
  };

  return (
    <div className="leave-calendar-wrapper bg-card rounded-lg border p-4">
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek"
        }}
        events={events}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        height="auto"
        eventDisplay="block"
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short"
        }}
        dayMaxEvents={3}
        moreLinkClick="popover"
      />
      <style>{`
        .leave-calendar-wrapper .fc {
          --fc-border-color: hsl(var(--border));
          --fc-button-bg-color: hsl(var(--muted));
          --fc-button-border-color: hsl(var(--border));
          --fc-button-text-color: hsl(var(--foreground));
          --fc-button-hover-bg-color: hsl(var(--accent));
          --fc-button-hover-border-color: hsl(var(--border));
          --fc-button-active-bg-color: hsl(var(--primary));
          --fc-button-active-border-color: hsl(var(--primary));
          --fc-today-bg-color: hsl(var(--accent) / 0.3);
          --fc-page-bg-color: transparent;
          --fc-neutral-bg-color: hsl(var(--card));
          --fc-list-event-hover-bg-color: hsl(var(--accent));
        }
        .leave-calendar-wrapper .fc-toolbar-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: hsl(var(--foreground));
        }
        .leave-calendar-wrapper .fc-col-header-cell-cushion,
        .leave-calendar-wrapper .fc-daygrid-day-number {
          color: hsl(var(--foreground));
        }
        /* Weekend shading */
        .leave-calendar-wrapper .fc-day-sat,
        .leave-calendar-wrapper .fc-day-sun {
          background-color: rgba(100, 100, 100, 0.15) !important;
        }
        .leave-calendar-wrapper .fc-day-sat .fc-daygrid-day-number,
        .leave-calendar-wrapper .fc-day-sun .fc-daygrid-day-number {
          color: hsl(var(--muted-foreground));
        }
        .leave-calendar-wrapper .fc-col-header-cell.fc-day-sat,
        .leave-calendar-wrapper .fc-col-header-cell.fc-day-sun {
          background-color: rgba(100, 100, 100, 0.1) !important;
        }
        .leave-calendar-wrapper .fc-event {
          border-radius: 4px;
          font-size: 0.75rem;
          padding: 2px 6px;
          cursor: pointer;
          background-color: white !important;
          border-left-width: 3px !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .leave-calendar-wrapper .fc-event .fc-event-main {
          color: #000000 !important;
          font-weight: 500;
        }
        .leave-calendar-wrapper .fc-event .fc-event-title {
          color: #000000 !important;
        }
        .leave-calendar-wrapper .fc-daygrid-day.fc-day-today {
          background-color: hsl(var(--accent) / 0.2);
        }
        .leave-calendar-wrapper .fc-button {
          border-radius: 6px;
          font-size: 0.875rem;
          padding: 0.5rem 1rem;
          background-color: hsl(var(--muted)) !important;
          border: 1px solid hsl(var(--border)) !important;
          color: hsl(var(--foreground)) !important;
          font-weight: 500;
        }
        .leave-calendar-wrapper .fc-button:hover {
          background-color: hsl(var(--accent)) !important;
        }
        .leave-calendar-wrapper .fc-button-primary:not(:disabled).fc-button-active,
        .leave-calendar-wrapper .fc-button-primary:not(:disabled):active {
          background-color: hsl(var(--primary)) !important;
          color: hsl(var(--primary-foreground)) !important;
          border-color: hsl(var(--primary)) !important;
        }
        .leave-calendar-wrapper .fc-button-group {
          gap: 2px;
        }
        .leave-calendar-wrapper .fc-toolbar {
          gap: 1rem;
        }
        .leave-calendar-wrapper .fc-toolbar-chunk {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        /* Week view weekend shading */
        .leave-calendar-wrapper .fc-timegrid-col.fc-day-sat,
        .leave-calendar-wrapper .fc-timegrid-col.fc-day-sun {
          background-color: rgba(100, 100, 100, 0.15) !important;
        }
      `}</style>
    </div>
  );
}

// Leave Request Card Component
function LeaveCard({ leave, onApprove, onDeny, onDelete, canApprove, isCOO }) {
  return (
    <Card className="hover:border-primary/30 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-3 h-3 rounded-full shrink-0" 
                style={{ backgroundColor: leave.leave_type_color }}
              />
              <span className="font-medium text-sm">{leave.leave_type_name}</span>
              {getStatusBadge(leave.status)}
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <User className="w-4 h-4" />
              <span className="font-medium text-foreground">{leave.resource_name}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <CalendarDays className="w-4 h-4" />
              <span>{formatDateRange(leave.start_date, leave.end_date)}</span>
              {!leave.is_all_day && leave.start_time && (
                <span className="text-xs">({leave.start_time} - {leave.end_time})</span>
              )}
            </div>
            
            {leave.notes && (
              <p className="text-sm text-muted-foreground line-clamp-2">{leave.notes}</p>
            )}
            
            {leave.decision_reason && leave.status !== "pending" && (
              <p className="text-xs text-muted-foreground mt-2 italic">
                "{leave.decision_reason}" - {leave.approved_by_name}
              </p>
            )}
          </div>
          
          <div className="flex flex-col gap-2">
            {leave.status === "pending" && canApprove && (
              <>
                <Button 
                  size="sm" 
                  onClick={() => onApprove(leave)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid={`approve-leave-${leave.id}`}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => onDeny(leave)}
                  data-testid={`deny-leave-${leave.id}`}
                >
                  <X className="w-4 h-4 mr-1" />
                  Deny
                </Button>
              </>
            )}
            {(isCOO || leave.status === "pending") && (
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => onDelete(leave)}
                className="text-muted-foreground hover:text-destructive"
                data-testid={`delete-leave-${leave.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Holiday Card Component
function HolidayCard({ holiday, onDelete, isCOO }) {
  return (
    <Card className="border-dashed">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: holiday.leave_type_color }}
              />
              <span className="font-medium">{holiday.name}</span>
              <Badge variant="outline" className="text-xs">
                <Building2 className="w-3 h-3 mr-1" />
                {holiday.leave_type === "national_holiday" ? "National" : "Company"} Holiday
              </Badge>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarDays className="w-4 h-4" />
              <span>{formatDateRange(holiday.start_date, holiday.end_date)}</span>
              {holiday.region && <span className="text-xs">({holiday.region})</span>}
            </div>
          </div>
          
          {isCOO && (
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => onDelete(holiday)}
              className="text-muted-foreground hover:text-destructive"
              data-testid={`delete-holiday-${holiday.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function LeavesView() {
  const { user } = useAuth();
  const pmTestMode = usePmTestMode();
  
  // Role checks - include PM Test Mode
  const actualRole = user?.role || "";
  const isActualPM = actualRole === "Program Manager" || actualRole === "Marketing PM";
  const isPM = isActualPM || pmTestMode?.isActive;
  const isCOO = actualRole === "COO" && !pmTestMode?.isActive;
  const isArtist = actualRole === "Artist";
  const canApprove = (actualRole === "COO" || actualRole === "Program Manager") && !pmTestMode?.isActive;
  const canViewAllBalances = (isCOO || actualRole === "Admin" || actualRole === "Accountant") && !pmTestMode?.isActive;
  
  // Filter view modes based on role - PMs see "My PTO" instead of "PTO Balances"
  const availableViewModes = VIEW_MODES.filter(mode => {
    if (mode.id === "balances") {
      // Replace "PTO Balances" tab - only admins can see all balances
      return canViewAllBalances;
    }
    return true;
  });
  
  // Add "My PTO" tab for PMs (shows only their own balance)
  if (isPM && !canViewAllBalances) {
    availableViewModes.push({ id: "my-pto", label: "My PTO", icon: Wallet });
  }
  
  const [viewMode, setViewMode] = useState("calendar");
  const [loading, setLoading] = useState(true);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [resources, setResources] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  
  // PTO Balances state
  const [ptoBalances, setPtoBalances] = useState([]);
  const [ptoYear, setPtoYear] = useState(new Date().getFullYear());
  const [editingPto, setEditingPto] = useState(null);
  const [ptoForm, setPtoForm] = useState({ total_days: 15 });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [resourceTypeFilter, setResourceTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialogs
  const [showNewLeaveDialog, setShowNewLeaveDialog] = useState(false);
  const [showNewHolidayDialog, setShowNewHolidayDialog] = useState(false);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [decisionAction, setDecisionAction] = useState(null);
  const [decisionReason, setDecisionReason] = useState("");
  
  // New leave form
  const [newLeave, setNewLeave] = useState({
    resource_id: "",
    leave_type: "paid_time_off",
    start_date: "",
    end_date: "",
    is_all_day: true,
    start_time: "",
    end_time: "",
    notes: ""
  });
  
  // New holiday form
  const [newHoliday, setNewHoliday] = useState({
    name: "",
    start_date: "",
    end_date: "",
    leave_type: "company_holiday",
    region: "",
    notes: ""
  });
  
  // OT entries state
  const [otEntries, setOtEntries] = useState([]);
  const [otDateRange, setOtDateRange] = useState({ 
    start: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // PM OT Request state
  const [showOtRequestDialog, setShowOtRequestDialog] = useState(false);
  const [otRequestForm, setOtRequestForm] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: 2,
    program_id: "",
    notes: "",
    rate_override: 15  // Default $15 for PMs
  });
  const [programs, setPrograms] = useState([]);
  
  // My PTO state (for PMs)
  const [myPtoBalance, setMyPtoBalance] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        apiClient.get("/leaves"),
        apiClient.get("/holidays"),
        apiClient.get("/leave-types"),
        apiClient.get("/resources"),
        apiClient.get("/leaves/pending/count"),
        apiClient.get(`/ot-entries?start_date=${otDateRange.start}&end_date=${otDateRange.end}`).catch(() => ({ data: [] })),
        apiClient.get("/programs").catch(() => ({ data: [] }))  // For OT program selection
      ];
      
      // Only fetch all PTO balances if user can view them
      if (canViewAllBalances) {
        requests.push(apiClient.get(`/pto-balances?year=${ptoYear}`).catch(() => ({ data: { balances: [] } })));
      }
      
      const results = await Promise.all(requests);
      
      setLeaves(results[0].data);
      setHolidays(results[1].data);
      setLeaveTypes(results[2].data);
      setResources(results[3].data);
      setPendingCount(results[4].data.count);
      setOtEntries(results[5].data || []);
      setPrograms(results[6].data || []);
      
      if (canViewAllBalances && results[7]) {
        setPtoBalances(results[7].data?.balances || []);
      }
      
      // For PMs, fetch their own PTO balance
      if (isPM && user?.id) {
        try {
          const myPtoRes = await apiClient.get(`/pto-balances/${user.id}?year=${ptoYear}`);
          setMyPtoBalance(myPtoRes.data);
        } catch {
          // PM may not have a resource entry yet
          setMyPtoBalance(null);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load leave data");
    } finally {
      setLoading(false);
    }
  }, [ptoYear, canViewAllBalances, isPM, user?.id, otDateRange.start, otDateRange.end]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter leaves
  const filteredLeaves = leaves.filter(leave => {
    if (statusFilter !== "all" && leave.status !== statusFilter) return false;
    if (resourceTypeFilter !== "all" && leave.resource_type !== resourceTypeFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        leave.resource_name?.toLowerCase().includes(query) ||
        leave.leave_type_name?.toLowerCase().includes(query) ||
        leave.notes?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Pending leaves only
  const pendingLeaves = leaves.filter(l => l.status === "pending");

  // Helper to add one day to a date string for FullCalendar (end date is exclusive)
  const getExclusiveEndDate = (dateStr) => {
    if (!dateStr) return dateStr;
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  // Calendar events
  const calendarEvents = [
    ...leaves.filter(l => l.status === "approved").map(leave => ({
      id: leave.id,
      title: `● ${leave.resource_name} - ${leave.leave_type_name}`,
      start: leave.start_date,
      end: getExclusiveEndDate(leave.end_date),
      allDay: leave.is_all_day,
      backgroundColor: '#ffffff',
      borderColor: leave.leave_type_color,
      textColor: '#000000',
      extendedProps: { ...leave, type: "leave", dotColor: leave.leave_type_color }
    })),
    ...holidays.map(holiday => ({
      id: holiday.id,
      title: `★ ${holiday.name}`,
      start: holiday.start_date,
      end: getExclusiveEndDate(holiday.end_date),
      allDay: true,
      backgroundColor: '#ffffff',
      borderColor: holiday.leave_type_color,
      textColor: '#000000',
      extendedProps: { ...holiday, type: "holiday", dotColor: holiday.leave_type_color }
    }))
  ];

  const handleCreateLeave = async () => {
    if (!newLeave.resource_id || !newLeave.start_date || !newLeave.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      await apiClient.post("/leaves", newLeave);
      toast.success("Leave request created");
      setShowNewLeaveDialog(false);
      setNewLeave({
        resource_id: "",
        leave_type: "paid_time_off",
        start_date: "",
        end_date: "",
        is_all_day: true,
        start_time: "",
        end_time: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create leave request");
    }
  };

  const handleCreateHoliday = async () => {
    if (!newHoliday.name || !newHoliday.start_date || !newHoliday.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      await apiClient.post("/holidays", newHoliday);
      toast.success("Holiday created");
      setShowNewHolidayDialog(false);
      setNewHoliday({
        name: "",
        start_date: "",
        end_date: "",
        leave_type: "company_holiday",
        region: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create holiday");
    }
  };

  const handleApprove = (leave) => {
    setSelectedLeave(leave);
    setDecisionAction("approve");
    setDecisionReason("");
    setShowDecisionDialog(true);
  };

  const handleDeny = (leave) => {
    setSelectedLeave(leave);
    setDecisionAction("deny");
    setDecisionReason("");
    setShowDecisionDialog(true);
  };

  const handleDecisionSubmit = async () => {
    if (!selectedLeave) return;
    
    try {
      await apiClient.post(`/leaves/${selectedLeave.id}/${decisionAction}`, {
        reason: decisionReason
      });
      toast.success(`Leave request ${decisionAction === "approve" ? "approved" : "denied"}`);
      setShowDecisionDialog(false);
      setSelectedLeave(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${decisionAction} leave request`);
    }
  };

  const handleDeleteLeave = async (leave) => {
    if (!confirm(`Are you sure you want to delete this leave request?`)) return;
    
    try {
      await apiClient.delete(`/leaves/${leave.id}`);
      toast.success("Leave request deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete leave request");
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    if (!confirm(`Are you sure you want to delete "${holiday.name}"?`)) return;
    
    try {
      await apiClient.delete(`/holidays/${holiday.id}`);
      toast.success("Holiday deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete holiday");
    }
  };

  const handleEventClick = (eventData) => {
    if (eventData.type === "leave") {
      const leave = leaves.find(l => l.id === eventData.id);
      if (leave) {
        setSelectedLeave(leave);
      }
    }
  };

  const handleDateClick = (dateStr) => {
    setNewLeave(prev => ({
      ...prev,
      start_date: dateStr,
      end_date: dateStr
    }));
    setShowNewLeaveDialog(true);
  };

  // PTO Balance handlers
  const handleUpdatePtoBalance = async () => {
    if (!editingPto) return;
    
    try {
      await apiClient.post("/pto-balances", {
        resource_id: editingPto.resource_id,
        year: ptoYear,
        total_days: ptoForm.total_days
      });
      toast.success("PTO balance updated");
      setEditingPto(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update PTO balance");
    }
  };

  const startEditPto = (balance) => {
    setEditingPto(balance);
    setPtoForm({ total_days: balance.total_days || balance.available_days || 15 });
  };

  return (
    <div className="space-y-4" data-testid="leaves-view">
      {/* Compact Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
            Calendar
          </h1>
          {/* Inline Stats - Apple-esque pill style */}
          <div className="hidden md:flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
              <span className="font-medium text-green-600">{leaves.filter(l => l.status === "approved").length}</span>
              <span className="text-green-600/70 text-xs">approved</span>
            </span>
            {pendingCount > 0 && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 rounded-full">
                <Clock className="w-3.5 h-3.5 text-yellow-600" />
                <span className="font-medium text-yellow-600">{pendingCount}</span>
                <span className="text-yellow-600/70 text-xs">pending</span>
              </span>
            )}
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-secondary/60 rounded-full">
              <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium">{holidays.length}</span>
              <span className="text-muted-foreground text-xs">holidays</span>
            </span>
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-secondary/60 rounded-full">
              <User className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium">{resources.length}</span>
              <span className="text-muted-foreground text-xs">team</span>
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          
          {isCOO && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowNewHolidayDialog(true)}
              data-testid="add-holiday-btn"
            >
              <Building2 className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Holiday</span>
            </Button>
          )}
          
          <Button
            size="sm"
            onClick={() => setShowNewLeaveDialog(true)}
            data-testid="new-leave-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Request Leave
          </Button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <TabsList>
            {availableViewModes.map(mode => (
              <TabsTrigger 
                key={mode.id} 
                value={mode.id}
                className="flex items-center gap-1.5 text-xs"
                data-testid={`tab-${mode.id}`}
              >
                <mode.icon className="w-3.5 h-3.5" />
                {mode.label}
                {mode.id === "pending" && pendingCount > 0 && (
                  <Badge variant="secondary" className="ml-1 bg-yellow-500/20 text-yellow-500 text-[10px] px-1.5">
                    {pendingCount}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {viewMode === "list" && (
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-[200px]"
                data-testid="search-input"
              />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="denied">Denied</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Calendar View */}
        <TabsContent value="calendar" className="mt-6">
          <LeaveCalendar
            events={calendarEvents}
            onEventClick={handleEventClick}
            onDateClick={handleDateClick}
            loading={loading}
          />
          
          {/* Holidays List */}
          {holidays.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Upcoming Holidays</h3>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {holidays.slice(0, 6).map(holiday => (
                  <HolidayCard
                    key={holiday.id}
                    holiday={holiday}
                    onDelete={handleDeleteHoliday}
                    isCOO={isCOO}
                  />
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* 3-Month Outlook View */}
        <TabsContent value="outlook" className="mt-6">
          <ThreeMonthOutlook 
            leaves={leaves} 
            resources={resources}
            holidays={holidays}
          />
        </TabsContent>

        {/* List View */}
        <TabsContent value="list" className="mt-6">
          <div className="space-y-3">
            {filteredLeaves.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No leave requests found</p>
                </CardContent>
              </Card>
            ) : (
              filteredLeaves.map(leave => (
                <LeaveCard
                  key={leave.id}
                  leave={leave}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  onDelete={handleDeleteLeave}
                  canApprove={canApprove}
                  isCOO={isCOO}
                />
              ))
            )}
          </div>
        </TabsContent>

        {/* Pending Requests View */}
        <TabsContent value="pending" className="mt-6">
          {!canApprove ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Only COO and Program Managers can view pending requests</p>
              </CardContent>
            </Card>
          ) : pendingLeaves.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                <p className="text-lg font-medium">All caught up!</p>
                <p className="text-sm">No pending leave requests to review</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingLeaves.map(leave => (
                <LeaveCard
                  key={leave.id}
                  leave={leave}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  onDelete={handleDeleteLeave}
                  canApprove={canApprove}
                  isCOO={isCOO}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* PTO Balances View */}
        <TabsContent value="balances" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Leave Balances</CardTitle>
                  <p className="text-sm text-muted-foreground">Manage team PTO and National Holiday allocations</p>
                </div>
                <Select value={ptoYear.toString()} onValueChange={(v) => setPtoYear(parseInt(v))}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {ptoBalances.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Wallet className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No leave balances configured for {ptoYear}</p>
                  <p className="text-sm mt-2">Leave balances will appear here once resources have allocations set</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ptoBalances.map(balance => (
                    <div 
                      key={balance.resource_id} 
                      className="p-4 border rounded-lg hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{balance.resource_name}</p>
                            <p className="text-sm text-muted-foreground">{balance.resource_type}</p>
                          </div>
                        </div>
                        {isCOO && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => startEditPto(balance)}
                            data-testid={`edit-pto-${balance.resource_id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      {/* PTO Row */}
                      <div className="flex items-center justify-between py-2 border-t">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                          <span className="text-sm font-medium">PTO</span>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Annual</p>
                            <p className="font-semibold text-sm">{balance.total_days || balance.available_days} days</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Used</p>
                            <p className="font-semibold text-sm text-orange-600">{balance.used_days} days</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Remaining</p>
                            <p className={`font-semibold text-sm ${balance.remaining_days >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {balance.remaining_days} days
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* National Holidays Row */}
                      <div className="flex items-center justify-between py-2 border-t">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                          <span className="text-sm font-medium">National Holidays</span>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Annual</p>
                            <p className="font-semibold text-sm">{balance.national_holiday_total || 12} days</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Used</p>
                            <p className="font-semibold text-sm text-orange-600">{balance.national_holiday_used || 0} days</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Remaining</p>
                            <p className={`font-semibold text-sm ${(balance.national_holiday_remaining ?? 12) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {balance.national_holiday_remaining ?? 12} days
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My PTO Tab (for PMs) */}
        {isPM && (
          <TabsContent value="my-pto" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>My PTO Balance</CardTitle>
              </CardHeader>
              <CardContent>
                {myPtoBalance ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 border rounded-lg text-center">
                        <p className="text-2xl font-light text-green-600">{myPtoBalance.total_days || 15}</p>
                        <p className="text-sm text-muted-foreground">Total Days</p>
                      </div>
                      <div className="p-4 border rounded-lg text-center">
                        <p className="text-2xl font-light text-orange-600">{myPtoBalance.used_days || 0}</p>
                        <p className="text-sm text-muted-foreground">Used</p>
                      </div>
                      <div className="p-4 border rounded-lg text-center">
                        <p className="text-2xl font-light text-blue-600">{(myPtoBalance.total_days || 15) - (myPtoBalance.used_days || 0)}</p>
                        <p className="text-sm text-muted-foreground">Remaining</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      Year: {ptoYear}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wallet className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>PTO balance not yet configured</p>
                    <p className="text-xs mt-2">Contact admin to set up your PTO allocation</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* New Leave Request Dialog */}
      <Dialog open={showNewLeaveDialog} onOpenChange={setShowNewLeaveDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription>
              Submit a new leave request for yourself or a team member.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Team Member *</Label>
              <Select 
                value={newLeave.resource_id} 
                onValueChange={(v) => setNewLeave({ ...newLeave, resource_id: v })}
              >
                <SelectTrigger data-testid="resource-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {resources.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select 
                value={newLeave.leave_type} 
                onValueChange={(v) => setNewLeave({ ...newLeave, leave_type: v })}
              >
                <SelectTrigger data-testid="leave-type-select">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.filter(t => t.id !== "company_holiday").map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: type.color }}
                        />
                        {type.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={newLeave.start_date}
                  onChange={(e) => setNewLeave({ ...newLeave, start_date: e.target.value })}
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={newLeave.end_date}
                  onChange={(e) => setNewLeave({ ...newLeave, end_date: e.target.value })}
                  data-testid="end-date-input"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="all-day"
                checked={newLeave.is_all_day}
                onCheckedChange={(checked) => setNewLeave({ ...newLeave, is_all_day: checked })}
              />
              <Label htmlFor="all-day" className="cursor-pointer">All day</Label>
            </div>
            
            {!newLeave.is_all_day && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={newLeave.start_time}
                    onChange={(e) => setNewLeave({ ...newLeave, start_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={newLeave.end_time}
                    onChange={(e) => setNewLeave({ ...newLeave, end_time: e.target.value })}
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newLeave.notes}
                onChange={(e) => setNewLeave({ ...newLeave, notes: e.target.value })}
                placeholder="Optional notes about your leave request..."
                rows={3}
                data-testid="notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLeaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateLeave} data-testid="submit-leave-btn">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Holiday Dialog */}
      <Dialog open={showNewHolidayDialog} onOpenChange={setShowNewHolidayDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Holiday</DialogTitle>
            <DialogDescription>
              Create a company-wide or national holiday.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Holiday Name *</Label>
              <Input
                value={newHoliday.name}
                onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                placeholder="e.g., Christmas Day"
                data-testid="holiday-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Holiday Type *</Label>
              <Select 
                value={newHoliday.leave_type} 
                onValueChange={(v) => setNewHoliday({ ...newHoliday, leave_type: v })}
              >
                <SelectTrigger data-testid="holiday-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company_holiday">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      Company Holiday
                    </div>
                  </SelectItem>
                  <SelectItem value="national_holiday">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      National Holiday
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={newHoliday.start_date}
                  onChange={(e) => setNewHoliday({ ...newHoliday, start_date: e.target.value })}
                  data-testid="holiday-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={newHoliday.end_date}
                  onChange={(e) => setNewHoliday({ ...newHoliday, end_date: e.target.value })}
                  data-testid="holiday-end-date"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Region (Optional)</Label>
              <Input
                value={newHoliday.region}
                onChange={(e) => setNewHoliday({ ...newHoliday, region: e.target.value })}
                placeholder="e.g., USA, Global"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newHoliday.notes}
                onChange={(e) => setNewHoliday({ ...newHoliday, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewHolidayDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateHoliday} data-testid="submit-holiday-btn">
              Create Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decision Dialog (Approve/Deny) */}
      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              {decisionAction === "approve" ? "Approve" : "Deny"} Leave Request
            </DialogTitle>
            <DialogDescription>
              {selectedLeave && (
                <>
                  {selectedLeave.resource_name}'s request for {selectedLeave.leave_type_name}
                  <br />
                  {formatDateRange(selectedLeave?.start_date, selectedLeave?.end_date)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason {decisionAction === "deny" && "(Required)"}</Label>
              <Textarea
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                placeholder={decisionAction === "approve" ? "Optional approval note..." : "Please provide a reason for denial..."}
                rows={3}
                data-testid="decision-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDecisionSubmit}
              className={decisionAction === "approve" ? "bg-green-600 hover:bg-green-700" : ""}
              variant={decisionAction === "deny" ? "destructive" : "default"}
              disabled={decisionAction === "deny" && !decisionReason.trim()}
              data-testid="confirm-decision-btn"
            >
              {decisionAction === "approve" ? "Approve" : "Deny"} Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit PTO Balance Dialog */}
      <Dialog open={!!editingPto} onOpenChange={() => setEditingPto(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit PTO Balance - {editingPto?.resource_name}</DialogTitle>
            <DialogDescription>
              Update PTO allocation for {ptoYear}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Annual PTO Days</Label>
              <Input
                type="number"
                min="0"
                max="365"
                value={ptoForm.total_days}
                onChange={(e) => setPtoForm({ total_days: parseInt(e.target.value) || 0 })}
                data-testid="pto-days-input"
              />
            </div>
            {editingPto && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-secondary/30 rounded-lg text-sm">
                <div>
                  <p className="text-muted-foreground">Used Days</p>
                  <p className="font-semibold">{editingPto.used_days} days</p>
                </div>
                <div>
                  <p className="text-muted-foreground">New Remaining</p>
                  <p className="font-semibold">{ptoForm.total_days - (editingPto.used_days || 0)} days</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPto(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePtoBalance} data-testid="save-pto-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
